/**
 * Справочник банковских счетов (карточка BL-2026-08-15-2024, §7.1 п. 4).
 *
 * Flow: GET /api/treasury/bank-accounts → собственные и неразмеченные счета
 *       одной таблицей, клиентские — ОТДЕЛЬНОЙ ниже → щелчок по счёту
 *       открывает его операции → администратор меняет принадлежность денег
 *       с обязательным комментарием (PUT …/funds-owner).
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadBankAccounts, setBankAccountOwner;
 *   - ./BankStatementCommon: бейджи и форматтеры.
 *
 * Связанные файлы:
 *   - src/BankStatementsSection.jsx — размещает справочник и операции счёта;
 *   - src/BankTransactionsPanel.jsx — операции выбранного счёта;
 *   - docs/specs/BL-2026-08-15-2024-bank-statements.md — §3.2, §6.7, §6.8.
 *
 * # ПОЧЕМУ ПРАВКА ТОЛЬКО У АДМИНИСТРАТОРА
 *
 * Разметка решает, войдут ли деньги счёта в собственную ликвидность, — тот же
 * класс решений, что коэффициент покрытия (DEC-031); сервер держит это
 * предикатом `CanConfigure`. Здесь правка просто не предлагается тем, кому
 * сервер её запретит.
 *
 * Справочник перечитывается и по refreshKey, и после правки разметки —
 * два запроса могут разминуться, поэтому применяется только ответ на
 * последний (useLatestRequest), иначе поздний ответ вернул бы на экран
 * разметку до правки.
 */

import { useCallback, useEffect, useState } from "react";

import { loadBankAccounts, setBankAccountOwner } from "./api";
import {
  OWNER_META,
  OwnerBadge,
  formatAmount,
  formatCut,
  rowKey,
  useLatestRequest,
} from "./BankStatementCommon";

/**
 * Правка принадлежности денег на счёте (только admin).
 *
 * @param {object} account — счёт §6.7
 * @param {function(object): void} onSaved — счёт после правки
 */
export function OwnerEditor({ account, onSaved }) {
  const [owner, setOwner] = useState(account.funds_owner);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!comment.trim()) {
      setError("Комментарий обязателен — основание разметки пишется в журнал.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const saved = await setBankAccountOwner(account.account_id, owner, comment.trim());
      setComment("");
      onSaved(saved);
    } catch (err) {
      setError(err.message);
      console.error("принадлежность счёта не сохранена", account.account_id, err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="inline" onSubmit={save} onClick={(e) => e.stopPropagation()}>
      <select
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
        aria-label={`принадлежность ${account.account_number}`}
        disabled={busy}
      >
        {Object.entries(OWNER_META).map(([code, meta]) => (
          <option key={code} value={code}>
            {meta.title}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={comment}
        placeholder="основание"
        aria-label={`основание ${account.account_number}`}
        onChange={(e) => setComment(e.target.value)}
        disabled={busy}
      />
      <button type="submit" disabled={busy || owner === account.funds_owner}>
        {busy ? "…" : "Сохранить"}
      </button>
      {error && <span className="error-text">{error}</span>}
    </form>
  );
}

/**
 * Таблица счетов одной группы.
 */
function AccountsTable({ accounts, isAdmin, selectedId, onSelect, onSaved }) {
  return (
    <table className="bs-table">
      <thead>
        <tr>
          <th>Банк</th>
          <th>Счёт</th>
          <th>Валюта</th>
          <th>Принадлежность</th>
          <th className="num">Остаток</th>
          <th>Срез</th>
        </tr>
      </thead>
      <tbody>
        {accounts.map((a) => (
          <tr
            key={rowKey(a.account_id, "acc", a.bank, a.account_number, a.currency)}
            className={`bs-row-click ${
              a.account_id !== null && a.account_id !== undefined && a.account_id === selectedId
                ? "bs-row-selected"
                : ""
            }`}
            onClick={() => onSelect(a)}
            title="показать операции счёта"
          >
            <td>{a.bank_title || a.bank}</td>
            <td>
              {a.account_number}
              {a.iban && <div className="cell-note muted">{a.iban}</div>}
              {a.holder_name && <div className="cell-note muted">{a.holder_name}</div>}
            </td>
            <td>{a.currency}</td>
            <td>
              <OwnerBadge owner={a.funds_owner} />
              {a.owner_comment && (
                <div className="cell-note muted" title={`основание: ${a.owner_basis || "—"}`}>
                  {a.owner_comment}
                </div>
              )}
              {/* Разметка пишется по account_id: без него править нечего. */}
              {isAdmin && a.account_id !== null && a.account_id !== undefined && (
                <OwnerEditor account={a} onSaved={onSaved} />
              )}
            </td>
            <td className="num">
              {a.balance === null || a.balance === undefined ? (
                // Не ноль и не прочерк: по счёту не принято ни одной
                // выписки, и это нужно сказать словами (BR-011).
                <span className="state-unknown">нет выписки</span>
              ) : (
                formatAmount(a.balance)
              )}
            </td>
            <td>
              {a.balance_at ? formatCut(a.balance_at, a.cut_precision) : "—"}
              {a.is_stale && <div className="cell-note stale">устарел</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Справочник банковских счетов.
 *
 * @param {object} user — текущий пользователь; admin правит принадлежность
 * @param {number} refreshKey — меняется после загрузки выписки
 * @param {number|null} selectedId — выбранный счёт
 * @param {function(object): void} onSelect — выбрать счёт
 */
export default function BankAccountsPanel({ user, refreshKey = 0, selectedId = null, onSelect }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState("");
  const isAdmin = user?.role === "admin";
  const begin = useLatestRequest();

  const load = useCallback(() => {
    const isCurrent = begin();
    loadBankAccounts()
      .then((d) => {
        if (!isCurrent()) return;
        setAccounts(d.accounts || []);
        setError("");
      })
      .catch((e) => {
        if (!isCurrent()) return;
        setError(e.message);
        console.error("справочник банковских счетов не загрузился", e);
      });
  }, [begin]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const onSaved = useCallback(
    (saved) => {
      // Правка на месте по ответу сервера + перечитывание: разметка меняет
      // и находки, и итоги, и строка из ответа — лишь первая из перемен.
      setAccounts((prev) =>
        (prev || []).map((a) => (a.account_id === saved.account_id ? { ...a, ...saved } : a)),
      );
      load();
    },
    [load],
  );

  if (error && !accounts) {
    return (
      <section className="card">
        <h2>Банковские счета</h2>
        <p className="error">{error}</p>
      </section>
    );
  }
  if (!accounts) {
    return (
      <section className="card">
        <h2>Банковские счета</h2>
        <p className="muted">Загрузка…</p>
      </section>
    );
  }

  const own = accounts.filter((a) => a.funds_owner !== "client");
  const client = accounts.filter((a) => a.funds_owner === "client");
  const tableProps = { isAdmin, selectedId, onSelect, onSaved };

  return (
    <section className="card">
      <h2>Банковские счета</h2>
      {error && <p className="error">Справочник не обновился: {error}</p>}
      {accounts.length === 0 ? (
        <p className="muted">
          Банковских счетов ещё нет — они заводятся первой загруженной выпиской.
        </p>
      ) : (
        <>
          {own.length > 0 && <AccountsTable accounts={own} {...tableProps} />}
          {client.length > 0 && (
            <div className="bs-client-group">
              <h3>Клиентские счета — не ликвидность компании</h3>
              <p className="muted cell-note">
                Деньги клиентов на сегрегированных счетах: обеспечивают
                обязательство перед клиентами и в свободную ликвидность не входят.
              </p>
              <AccountsTable accounts={client} {...tableProps} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
